import { useTranslation } from 'react-i18next';

import type { BigHeaderType, HeaderType } from 'components/stateless/Table/TableHeader';
import type { StatisticsOfAnchorAgreementVOModel } from 'models/vo/StatisticsOfAnchorAgreementVO';
import type { ColumnOption, MergedCellTypes } from 'utils/spreadSheet/types';

export function useSystemOverviewByAnchorListVendorTabConstants() {
  const { t } = useTranslation(['format']);

  const AR_TABLE_HEADER: HeaderType[] = [
    {
      headerText: t('text:Financier_Name'),
    },
    {
      headerText: t('text:Anchor_Name'),
    },
    {
      headerText: t('text:Anchor_Client_Code'),
    },
    {
      headerText: t('text:Anchor_Master_Agreement_Number'),
    },
    {
      headerText: t('text:Currency'),
    },
    {
      headerText: t('text:Automatic_Settlement'),
    },
    {
      headerText: t('text:Financing_Balance_/_Count'),

      className: 'main100',
    },
    {
      headerText: t('text:Pending_AR_Amount_/_Count'),

      className: 'main100',
    },
    {
      headerText: t('text:Total_Registered_AR_Amount_/_Count'),

      className: 'main200',
    },
    {
      headerText: t('text:Total_Financing_Amount_/_Count'),

      className: 'main200',
    },
  ];
  const AR_BIG_TABLE_HEADER: BigHeaderType[] = [
    {
      headerText: t('text:Financier_Information'),
      colSpan: 1,
    },
    {
      headerText: t('text:Anchor_Information'),
      colSpan: 5,
    },
    {
      headerText: t('text:Associated_Partner_Current_Usage_Information'),
      colSpan: 2,
      className: 'main100',
    },
    {
      headerText: t('text:Associated_Partner_Accumulated_Usage_Information'),
      colSpan: 2,
      className: 'main200',
    },
  ];

  const excelColumnsAr: ColumnOption<StatisticsOfAnchorAgreementVOModel>[] = [
    {
      header: t('text:Financier_Name'),
      key: 'financierName',
    },
    {
      header: t('text:Anchor_Name'),
      key: 'anchorFinancierClientName',
    },
    {
      header: t('text:Anchor_Client_Code'),
      key: 'anchorFinancierClientCode',
    },
    {
      header: t('text:Anchor_Master_Agreement_Number'),
      key: 'contractNo',
    },
    {
      header: t('text:Currency'),
      key: 'currencyType',
    },
    {
      header: t('text:Automatic_Settlement'),
      key: 'paymentSupport',
    },
    {
      header: t('text:Outstanding_Financing_Balance'),
      key: 'currentLoanBalanceAmount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Number_of_Outstanding_Financing'),
      key: 'currentDisbursedLoanCount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Pending_AR_Amount'),
      key: 'currentSettlementWaitingInvoiceAmount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Number_of_Pending_AR'),
      key: 'currentSettlementWaitingInvoiceCount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Total_Registered_AR_Amount'),
      key: 'registeredInvoiceAmount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Total_Number_of_Registered_AR'),
      key: 'registeredInvoiceCount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Total_Financing_Amount'),
      key: 'totalDisbursedLoanAmount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
    {
      header: t('text:Total_Number_of_Financing'),
      key: 'totalDisbursedLoanCount',
      style: {
        alignment: { horizontal: 'right' },
      },
    },
  ];
  const arMergedCells: MergedCellTypes[] = [
    {
      target: 'A:A',
      rowIndex: 1,
      value: t('text:Financier_Information'),
    },
    {
      target: 'B:F',
      rowIndex: 1,
      value: t('text:Anchor_Information'),
    },
    {
      target: 'G:J',
      rowIndex: 1,
      value: t('text:Associated_Partner_Current_Usage_Information'),
      style: {
        fill: {
          fgColor: { argb: 'FFFFFCDE' },
        },
      },
    },
    {
      target: 'K:N',
      rowIndex: 1,
      value: t('text:Associated_Partner_Accumulated_Usage_Information'),
      style: {
        fill: {
          fgColor: { argb: 'FFFFF8BD' },
        },
      },
    },
  ];

  const arTableHeader: HeaderType[] = [
    {
      headerText: t('text:Financier_Name'),
    },
    {
      headerText: t('text:Anchor_Name'),
      className: 'main100',
    },
    {
      headerText: t('text:Anchor_Client_Code'),
      className: 'main100',
    },
    {
      headerText: t('text:Anchor_Master_Agreement_Number'),
      className: 'main100',
    },
    {
      headerText: t('text:Currency'),
      className: 'main100',
    },
    {
      headerText: t('text:Automatic_Settlement'),
      className: 'main100',
    },
    {
      headerText: t('text:Outstanding_Financing_Balance'),
      className: 'main200',
    },
    {
      headerText: t('text:Number_of_Outstanding_Financing'),
      className: 'main200',
    },
    {
      headerText: t('text:Pending_AR_Amount'),
      className: 'main200',
    },
    {
      headerText: t('text:Number_of_Pending_AR'),
      className: 'main200',
    },
    {
      headerText: t('text:Total_Registered_AR_Amount'),
      className: 'main300',
    },
    {
      headerText: t('text:Total_Number_of_Registered_AR'),
      className: 'main300',
    },
    {
      headerText: t('text:Total_Financing_Amount'),
      className: 'main300',
    },
    {
      headerText: t('text:Total_Number_of_Financing'),
      className: 'main300',
    },
  ];
  const arMergedTableHeader: BigHeaderType[] = [
    {
      headerText: t('text:Financier_Information'),
      colSpan: 1,
    },
    {
      headerText: t('text:Anchor_Information'),
      colSpan: 5,
      className: 'main100',
    },
    {
      headerText: t('text:Associated_Partner_Current_Usage_Information'),
      colSpan: 4,
      className: 'main200',
    },
    {
      headerText: t('text:Associated_Partner_Accumulated_Usage_Information'),
      colSpan: 4,
      className: 'main300',
    },
  ];

  return {
    AR_TABLE_HEADER,
    AR_BIG_TABLE_HEADER,
    excelColumnsAr,
    arMergedCells,
    arTableHeader,
    arMergedTableHeader,
  };
}
